// packages/core/src/diff/fields-aspects.ts
import type { FieldDecl, AspectEntry, Value } from "../types.js";
import type { FieldDiff, Status } from "../diff-types.js";
import { exprText } from "../selector-ast.js";

function pathKey(parts: readonly string[]): string {
  return parts.join(".");
}

function sameValue(a: Value | undefined, b: Value | undefined): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  if (a.kind !== b.kind) return false;
  switch (a.kind) {
    case "identifier":
    case "string":
      return a.text === (b as typeof a).text;
    case "ref":
      return (
        a.path.join(".") === (b as typeof a).path.join(".") &&
        a.instance === (b as typeof a).instance
      );
    case "number":
      return a.value === (b as typeof a).value;
    case "boolean":
      return a.value === (b as typeof a).value;
    case "list":
      if (a.items.length !== (b as typeof a).items.length) return false;
      return a.items.every((it, i) => sameValue(it, (b as typeof a).items[i]));
    case "expr":
      return exprText(a.expr) === exprText((b as typeof a).expr);
  }
}

export function diffFields(
  before: readonly FieldDecl[],
  after: readonly FieldDecl[]
): Map<string, FieldDiff> {
  return diffKeyValue(
    before.map((f) => [pathKey(f.path), f.value] as const),
    after.map((f) => [pathKey(f.path), f.value] as const)
  );
}

export function diffAspects(
  before: readonly AspectEntry[],
  after: readonly AspectEntry[]
): Map<string, FieldDiff> {
  return diffKeyValue(
    before.map((l) => [pathKey(l.path), l.value] as const),
    after.map((l) => [pathKey(l.path), l.value] as const)
  );
}

function diffKeyValue(
  before: readonly (readonly [string, Value | undefined])[],
  after: readonly (readonly [string, Value | undefined])[]
): Map<string, FieldDiff> {
  const beforeMap = new Map(before);
  const afterMap = new Map(after);
  const out = new Map<string, FieldDiff>();
  const all = new Set<string>([...beforeMap.keys(), ...afterMap.keys()]);
  for (const k of all) {
    const bv = beforeMap.get(k);
    const av = afterMap.get(k);
    const inBefore = beforeMap.has(k);
    const inAfter = afterMap.has(k);
    let status: Status = "unchanged";
    if (inBefore && !inAfter) status = "removed";
    else if (!inBefore && inAfter) status = "added";
    else if (!sameValue(bv, av)) status = "modified";
    if (status !== "unchanged") out.set(k, { status, from: bv, to: av });
  }
  return out;
}
